/**
 * Created by Ning Jiang on 2/7/2019.
 */

import { IResourcesGroupConfig } from "@nolimitcity/slot-game/bin/core/resource/asset/Asset";
import { IFontsConfig } from "@nolimitcity/slot-game/bin/core/resource/font/IFontsConfig";
import { ISoundConfig } from "@nolimitcity/slot-game/bin/core/resource/sound/ISoundConfig";
import { SanBackground } from "./presentations/background/SanBackground";
import { ReelOverlay } from "./presentations/background/ReelOverlay";

import { SpinEvent } from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import { WinPresentationEvent } from "@nolimitcity/slot-game/bin/core/winpresentation/event/WinPresentationEvent";
import { IGameModuleConfig } from "@nolimitcity/slot-game/bin/core/gamemoduleconfig/IGameModuleConfig";
import { IGameConfig } from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import { IndividualWinPresentationController } from "@nolimitcity/slot-game/bin/core/winpresentation/individualwin/IndividualWinPresentationController";
import { SanGameAssets } from "./SanGameAssets";
import { SoundConfig } from "./SoundConfig";
import { FontConfig } from "./FontConfig";
import { SanGameConfig } from "./SanGameConfig";

import { RestorePresentation } from "./presentations/features/restore/RestorePresentation";
import { TestPresentation } from "./presentations/features/TestPresentation";

import { ReelStartPresentation } from "./presentations/reel/ReelStartPresentation";
import { BonusIntroPresentation } from "./presentations/features/bonus/BonusIntroPresentation";

import { ReelStopPresentation } from "./presentations/reel/ReelStopPresentation";
import { ScatterRevealPresentation } from "./presentations/features/scatter/ScatterRevealPresentation";

import { ReelCorrection } from "./presentations/reel/ReelCorrection";
import { ReelStoppedPresentation } from "./presentations/reel/ReelStoppedPresentation";
import { IdleWinPresentation } from "./presentations/features/idle/IdleWinPresentation";
import { TotalWinPresentation } from "./presentations/features/outro/TotalWinPresentation";
import { BrokenBankOutro } from "./presentations/features/outro/BrokenBankOutro";

import { NormalWinPresentation } from "./presentations/features/normalwin/NormalWinPresentation";
import { BigWinPresentation } from "./presentations/features/bigwin/BigWinPresentation";

import { ISanInitData, ISanGameData } from "./server/data/ISanServerData";
import { SanParsedInitData } from "./server/data/SanParsedInitData";
import { SanParsedGameData } from "./server/data/SanParsedGameData";
import { ServerDataParser } from "@nolimitcity/slot-game/bin/core/server/ServerDataParser";

import { SanEngineManager } from "./engine/SanEngineManager";
import { WinCounter } from "./presentations/independent/countup/WinCounter";
import { ResizeHelper } from "@nolimitcity/slot-game/bin/core/utils/ResizeHelper";
import { RevealerManager } from "./presentations/independent/revealer/RevealerManager";
import { PosterManager } from "./presentations/independent/poster/PosterManager";
import { ReelAnimationManager } from "./presentations/independent/reelAnimator/ReelAnimationManager";

import { MiniPayTable } from "./engine/Symbol/MiniPayTable";

import { ParticlesManager } from "./presentations/independent/particles/ParticlesManager";

import { SanGameController } from "./SanGameController"
import { SoundManager } from "./resource/sound/SoundManager";
import { SplashController } from "./presentations/independent/splashScreen/SplashController";
import { JumpingWildController } from "./presentations/independent/jumpingWild/JumpingWildController";
import { XWaysCounter } from "./presentations/independent/countup/XWaysCounter";
import { JumpingWildCollector } from "./presentations/independent/collector/JumpingWildCollector";
import { IntroPresentation } from "./presentations/features/intro/IntroPresentation";
import { IntroAnimation } from "./presentations/independent/intro/IntroAnimation";
import { GameSetting } from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import { PostCounter } from "./presentations/independent/poster/PostCounter";
import {FeaturePrice} from "./utils/FeaturePrice";
import {ParsedInitData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedInitData";
import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";

export class SanGameModuleConfig implements IGameModuleConfig {

    private static resizeHelper: ResizeHelper = new ResizeHelper();
    private static soundManager: SoundManager = new SoundManager();

    public static instance: SanGameModuleConfig = new SanGameModuleConfig();
    private static EngineManager: SanEngineManager;
    private static background: SanBackground;
    private static revealer: RevealerManager;
    private static counterDisplayer: WinCounter;
    private static posterManager: PosterManager;
    private static reelAnimationManager: ReelAnimationManager;
    private static miniTable: MiniPayTable;
    private static splashController: SplashController;

    private static reelOverlay: ReelOverlay;

    private static particleManager: ParticlesManager;
    private static jumpingWildController: JumpingWildController;
    private static xWaysCounter:XWaysCounter;
    private static jumpingWildCollector:JumpingWildCollector;
    

    private static introAnimation:IntroAnimation;
    private static postCounter:PostCounter;

    public static featurePrice: FeaturePrice;

    GAME_CONFIG: IGameConfig = SanGameConfig.instance;
    SOUND_CONFIG: ISoundConfig = SoundConfig.instance;
    FONTS_CONFIG: IFontsConfig = FontConfig.FONTS;
    RESOURCES_CONFIG: IResourcesGroupConfig = SanGameAssets.RESOURCES_GROUPS;

    PARSED_INIT_DATA: (data: any, dataParser: ServerDataParser) => ParsedInitData = (data, dataParser: ServerDataParser) => {
        return new SanParsedInitData(<ISanInitData>data, dataParser);
    };

    PARSED_GAME_DATA: (data: any, dataParser: ServerDataParser) => ParsedGameData = (data, dataParser: ServerDataParser) => {
        return new SanParsedGameData(<ISanGameData>data, dataParser);
    };

    GAME_CONTROLLER: () => SanGameController = () => {
        return new SanGameController({
            checklog: false,
            developerTools: GameSetting.isDevMode,
            optimization:false,
            PRESENTATIONS: [
                {
                    /* The first PresentationList that triggers when restore has been defined. */
                    eventType: SpinEvent.RESTORE,
                    presentations: [new RestorePresentation()]
                },
                {
                    /* Not functional, conflicts with the current Replay function. */
                    eventType: SpinEvent.INTRO,
                    presentations: [new IntroPresentation()]
                },
                {
                    /* Prefer to use only one ReelStartPresentation. */
                    eventType: SpinEvent.START,
                    presentations: [new ReelStartPresentation()]
                },
                {
                    /* This is the only PresentationList that will be triggered multiple times from different events. 
                    * 1: When START is done.
                    * 2: When Data has been parsed.
                    * 3: When QuickStop Has been Triggered.
                    * 4: Retriggers the STOP is done. (This is an Old behaviour, this could be removed if we use this system in the future.)
                    */
                    eventType: SpinEvent.STOP,
                    presentations: [new ReelStopPresentation()]
                },
                {
                    /* 
                    * A normal TriggerList
                    */
                    eventType: WinPresentationEvent.WIN_PRESENTATIONS_STARTED,
                    presentations: [
                        new ReelCorrection(),
                        new ReelStoppedPresentation(),
                        new ScatterRevealPresentation(),
                        new NormalWinPresentation(),
                        new BigWinPresentation(),
                        new BrokenBankOutro(),
                        new BonusIntroPresentation(),
                        new TotalWinPresentation(),
                        new TestPresentation()
                    ]
                },
                {
                    /* When this has been played, this will then trigger FINISH. */
                    eventType: SpinEvent.IDLE,
                    presentations: [ new IdleWinPresentation() ]
                },
            ]
        });
    }

    GAME_FEATURE_CREATOR: () => void = () => {
        SanGameModuleConfig.EngineManager = new SanEngineManager(SanGameConfig.instance.REEL_ENGINE_CONFIG);

        SanGameModuleConfig.introAnimation = new IntroAnimation();
        SanGameModuleConfig.postCounter = new PostCounter();

        SanGameModuleConfig.revealer = new RevealerManager();
        SanGameModuleConfig.counterDisplayer = new WinCounter();
        SanGameModuleConfig.posterManager = new PosterManager();
        SanGameModuleConfig.reelAnimationManager = new ReelAnimationManager();
        SanGameModuleConfig.miniTable = new MiniPayTable({ 
            orientation_scale:{
                portrait:  new PIXI.Point(1,1),
                landscape: new PIXI.Point(0.9,0.9),
            },
            showBetValue: true,
        });

        SanGameModuleConfig.splashController = new SplashController();

        SanGameModuleConfig.reelOverlay = new ReelOverlay();

        SanGameModuleConfig.particleManager = new ParticlesManager();
        SanGameModuleConfig.jumpingWildController = new JumpingWildController();
        SanGameModuleConfig.xWaysCounter = new XWaysCounter();
        SanGameModuleConfig.jumpingWildCollector = new JumpingWildCollector();

        SanGameModuleConfig.background = new SanBackground({
            layer: SanGameConfig.instance.LAYERS.BACKGROUND.name,
            backgroundImages: {
                NORMAL: { image: SanGameAssets.MAIN_GAME_BACKGROUND, offset: new PIXI.Point(-480, -480) }
            },
            stretch: false
        });

        SanGameModuleConfig.featurePrice = new FeaturePrice();
    };

    INDIVIDUAL_WIN_PRESENTATION: () => IndividualWinPresentationController;

    constructor() {
        if (SanGameModuleConfig.instance) {
            throw new Error("SanGameModuleConfig.constructor() - Instantiation failed: Singleton.");
        }
    }
}